import React, {
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import { FetchContext } from 'src/context/FetchContext';
import { API_ROUTES } from 'src/services/constant';

const { PRIVATE_ROUTE } = API_ROUTES;

const StaffDwrView = ({
  open,
  onClose,
  id,
  onSuccess,
  onEdit,
}) => {
  const { authAxios } = useContext(FetchContext);

  const [dwrData, setDwrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);


  /* =========================================================
     STATUS
  ========================================================= */

  const getStatusLabel = (status) => {
    switch (Number(status)) {
      case 0:
        return 'Pending';

      case 1:
        return 'Approved';

      case 2:
        return 'Rejected';

      case 3:
        return 'Open';

      default:
        return 'Pending';
    }
  };

  const getStatusColor = (status) => {
    switch (Number(status)) {
      case 0:
        return 'warning';

      case 1:
        return 'success';

      case 2:
        return 'error';

      case 3:
        return 'info';

      default:
        return 'warning';
    }
  };

  const isPending =
    Number(dwrData?.status) === 0;

  /* =========================================================
     COMMON HELPERS
  ========================================================= */

  const getValue = (...values) => {
    const value = values.find(
      (item) =>
        item !== undefined &&
        item !== null &&
        String(item).trim() !== ''
    );

    return value !== undefined ? value : '-';
  };

  const formatDate = (value) => {
    if (!value || value === '-') {
      return '-';
    }

    const dateString = String(value);

    // Keep the calendar date from the API without
    // converting it through the browser's timezone.
    const datePart = dateString.substring(0, 10);

    const parts = datePart.split('-');

    if (parts.length !== 3) {
      return value;
    }

    const [year, month, day] = parts;

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(Number(year)) ||
      Number.isNaN(Number(month)) ||
      Number.isNaN(Number(day))
    ) {
      return value;
    }

    return `${month}/${day}/${year}`;
  };

  const formatSubmitDate = (value) => {
    if (!value || value === '-') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  /* =========================================================
     HEADER
  ========================================================= */

  const getJobNumber = () =>
    getValue(
      dwrData?.job_number,
      dwrData?.job_no,
      dwrData?.jobNumber,
      dwrData?.job?.job_number,
      dwrData?.job?.job_no,
      dwrData?.job?.jobNumber
    );

  const getTaskNumber = () =>
    getValue(
      dwrData?.task_number,
      dwrData?.task_no,
      dwrData?.taskNumber,

      /*
       * Actual DWR response structure.
       */
      dwrData?.taskdata?.number_str,
      dwrData?.taskdata?.number,
      dwrData?.taskdata?.task_number,
      dwrData?.taskdata?.task_no,

      dwrData?.task?.number_str,
      dwrData?.task?.task_number,
      dwrData?.task?.task_no
    );

  const getDwrNumber = () =>
    getValue(
      dwrData?.dwr_number,
      dwrData?.dwr_no,
      dwrData?.dwrNumber,
      dwrData?.number
    );

  /* =========================================================
     PROJECT MANAGER
  ========================================================= */

  const getProjectManager = () => {
    if (
      Array.isArray(dwrData?.project_managers) &&
      dwrData.project_managers.length > 0
    ) {
      const names = dwrData.project_managers
        .map((manager) => {
          if (!manager) {
            return null;
          }

          if (typeof manager === 'string') {
            return manager;
          }

          if (
            manager?.first_name ||
            manager?.last_name
          ) {
            return `${manager?.first_name || ''} ${manager?.last_name || ''
              }`.trim();
          }

          return getValue(
            manager?.name,
            manager?.full_name
          );
        })
        .filter(
          (name) =>
            name &&
            name !== '-'
        );

      if (names.length > 0) {
        return names.join(', ');
      }
    }

    return getValue(
      dwrData?.project_manager_name,
      dwrData?.projectManagerName,
      dwrData?.project_manager?.name,
      dwrData?.project_manager?.full_name,
      dwrData?.projectManager?.name,
      dwrData?.projectManager?.full_name
    );
  };

  /* =========================================================
     EMPLOYEE
  ========================================================= */

  const getEmployeeName = () => {
    if (
      Array.isArray(dwrData?.employees) &&
      dwrData.employees.length > 0
    ) {
      const employee =
        dwrData.employees[0];

      if (typeof employee === 'string') {
        return employee;
      }

      if (
        employee?.first_name ||
        employee?.last_name
      ) {
        return `${employee?.first_name || ''} ${employee?.last_name || ''
          }`.trim();
      }

      return getValue(
        employee?.name,
        employee?.full_name
      );
    }

    return getValue(
      dwrData?.employee_name,
      dwrData?.employeeName,
      dwrData?.employee?.name,
      dwrData?.employee?.full_name,
      dwrData?.assigned_member_name,
      dwrData?.assignedMemberName,

      /*
       * Labour cost employee name.
       */
      dwrData?.billing_line_items
        ?.labourCosts?.[0]
        ?.employee_name
    );
  };

  /* =========================================================
     COST ITEM
  ========================================================= */

  const getCostItem = () => {
    const labourCosts =
      dwrData?.billing_line_items
        ?.labourCosts ||
      dwrData?.billingLineItems
        ?.labourCosts;

    if (
      Array.isArray(labourCosts) &&
      labourCosts.length > 0
    ) {
      return getValue(
        labourCosts[0]?.costitem,
        labourCosts[0]?.costItem,
        labourCosts[0]?.cost_item
      );
    }

    return getValue(
      dwrData?.costitem,
      dwrData?.costItem,
      dwrData?.cost_item,
      dwrData?.billing_line_item?.costitem,
      dwrData?.billing_line_item?.costItem
    );
  };

  /* =========================================================
     TASK HOUR
  ========================================================= */

  const getTaskHour = () =>
    getValue(
      dwrData?.task_hour,
      dwrData?.taskHour,
      dwrData?.hours,
      dwrData?.dwr_hours,
      dwrData?.dwrHours,

      /*
       * Actual labour cost structure.
       */
      dwrData?.billing_line_items
        ?.labourCosts?.[0]
        ?.hours
    );

  /* =========================================================
     APPROVED HOURS
  ========================================================= */

  const getApprovedHours = () => {
    /*
     * Approved Hours should only be displayed when
     * the DWR itself is Approved.
     *
     * Status:
     * 0 = Pending
     * 1 = Approved
     * 2 = Rejected
     * 3 = Open
     */
    if (Number(dwrData?.status) !== 1) {
      return 'NA';
    }

    const estimateHour =
      dwrData?.billing_line_items
        ?.labourCosts?.[0]
        ?.estimate_hour;

    if (
      estimateHour === null ||
      estimateHour === undefined ||
      estimateHour === ''
    ) {
      return 'NA';
    }

    const numericHours =
      Number(estimateHour);

    if (!Number.isFinite(numericHours)) {
      return 'NA';
    }

    return numericHours.toFixed(2);
  };

  /* =========================================================
     REMARKS
  ========================================================= */

  const getRemarks = () =>
    getValue(
      dwrData?.remark,
      dwrData?.remarks,
      dwrData?.comments
    );

  /* =========================================================
     DESCRIPTION
  ========================================================= */

  const getDescription = () =>
    getValue(
      dwrData?.description,
      dwrData?.task_description,
      dwrData?.taskDescription,
      dwrData?.billing_line_items
        ?.labourCosts?.[0]
        ?.description
    );

  /* =========================================================
     GET DWR
  ========================================================= */

  const getDwr = useCallback(async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);

      const response =
        await authAxios.get(
          `${PRIVATE_ROUTE.GET_DWR_BY_ID}/${id}`
        );

      const responseData =
        response?.data?.data;

      const data =
        Array.isArray(responseData)
          ? responseData[0]
          : responseData;

      setDwrData(data || null);
    } catch (error) {
      console.error(
        'Error fetching DWR:',
        error
      );

      setDwrData(null);
    } finally {
      setLoading(false);
    }
  }, [authAxios, id]);

  useEffect(() => {
    if (open && id) {
      getDwr();
    }
  }, [
    open,
    id,
    getDwr,
  ]);

  useEffect(() => {
    if (!open) {
      setDwrData(null);
    }
  }, [open]);

  /* =========================================================
     BUILD SCHEDULE FOR SUBMIT DWR MODAL
  ========================================================= */

  const buildEditSchedule = () => {
    if (!dwrData) {
      return null;
    }

    const labourCost =
      dwrData?.billing_line_items
        ?.labourCosts?.[0] || {};

    const taskNumber =
      getTaskNumber();

    const jobNumber =
      getJobNumber();

    const costItem =
      getCostItem();

    const employeeName =
      getEmployeeName();

    const taskDate =
      getValue(
        dwrData?.task_date,
        dwrData?.taskDate,
        dwrData?.planned_date,
        dwrData?.plannedDate
      );

    /*
     * SubmitDwrModal expects a schedule-shaped
     * object:
     *
     * schedule.job_id.number_str
     * schedule.task.number_str
     * schedule.task._id
     * schedule.planned_date
     * schedule.task.billing_line_items...
     * schedule.assigned_members
     */
    return {
      ...dwrData,

      _id:
        dwrData?.schedule_id ||
        dwrData?.schedule?._id ||
        dwrData?._id,

      dwr_id:
        dwrData?._id,

      costItem:
        costItem === '-' ? '' : costItem,

      job_id: {
        ...(dwrData?.job_id || {}),
        number_str: jobNumber,
      },

      task: {
        ...(dwrData?.taskdata || {}),
        ...(dwrData?.task || {}),
        _id:
          dwrData?.task_id ||
          dwrData?.task?._id ||
          dwrData?.taskdata?._id,

        number_str: taskNumber,

        billing_line_items: {
          labour_item: {
            labour_cost_items: [
              {
                ...labourCost,
                costitem: costItem,
                costItem: costItem,
                hours:
                  getTaskHour() === '-'
                    ? ''
                    : getTaskHour(),
                employee_name:
                  employeeName,
                description:
                  getDescription() === '-'
                    ? ''
                    : getDescription(),
              },
            ],
          },
        },
      },

      planned_date:
        taskDate === '-'
          ? null
          : taskDate,

      assigned_members:
        dwrData?.assigned_members ||
        [
          {
            employee:
              labourCost?.employee ||
              dwrData?.user_id,
            employee_name:
              employeeName,
            dwr_id:
              dwrData?._id,
            dwr_status:
              Number(dwrData?.status) === 0
                ? 'pending'
                : Number(dwrData?.status) === 1
                  ? 'approved'
                  : Number(dwrData?.status) === 2
                    ? 'rejected'
                    : 'open',
          },
        ],
    };
  };

  /* =========================================================
     EDIT
  ========================================================= */

  const handleEdit = () => {
    if (!dwrData || !isPending) {
      return;
    }

    const schedule = buildEditSchedule();

    if (!schedule) {
      return;
    }

    // Send the schedule to StaffDwrlist
    if (typeof onEdit === 'function') {
      onEdit(schedule);
    }
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete = async () => {
    if (
      !dwrData ||
      !isPending ||
      deleteLoading
    ) {
      return;
    }

    const confirmed =
      window.confirm(
        'Are you sure you want to delete this DWR?'
      );

    if (!confirmed) {
      return;
    }

    try {
      setDeleteLoading(true);

      await authAxios.post(
        `${PRIVATE_ROUTE.DELETE_DWR}${dwrData?._id}`
      );

      if (
        typeof onSuccess === 'function'
      ) {
        await onSuccess();
      }

      onClose();
    } catch (error) {
      console.error(
        'Error deleting DWR:',
        error
      );

      window.alert(
        error?.response?.data?.message ||
        'Unable to delete the DWR. Please try again.'
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  /* =========================================================
     DETAIL ROW
  ========================================================= */

  const DetailRow = ({
    label,
    value,
    last = false,
  }) => (
    <Box
      sx={{
        py: 1.05,
        borderBottom: last
          ? 'none'
          : '1px solid #e1e6e8',
      }}
    >
      <Typography
        sx={{
          fontSize: '11px',
          color: '#607d8b',
          lineHeight: 1.2,
          mb: 0.35,
        }}
      >
        {label}
      </Typography>

      <Typography
        sx={{
          fontSize: '14px',
          color: '#263238',
          fontWeight: 500,
          lineHeight: 1.35,
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <>
      <Dialog
        open={Boolean(open)}
        onClose={onClose}
        fullWidth
        maxWidth="xs"
        scroll="paper"
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: 390,
            margin: '16px',
            borderRadius: '8px',
            overflow: 'hidden',
          },
        }}
      >
        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            backgroundColor: '#63c6cf',
            color: '#fff',
            px: 2,
            pt: 1.5,
            pb: 1.25,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'space-between',
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '18px',
                  fontWeight: 700,
                  lineHeight: 1.2,
                }}
              >
                View DWR
              </Typography>

              <Typography
                sx={{
                  mt: 0.55,
                  fontSize: '12px',
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                Job #{' '}
                {getJobNumber()} | Task #{' '}
                {getTaskNumber()}
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              size="small"
              sx={{
                color: '#fff',
                mt: -0.5,
                mr: -0.5,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* =================================================
            DWR NUMBER / STATUS
        ================================================= */}

        <Box
          sx={{
            px: 2,
            py: 1.25,
            backgroundColor: '#fff',
            borderBottom:
              '1px solid #e0e0e0',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#607d8b',
              }}
            >
              DWR No #{' '}
              {getDwrNumber()}
            </Typography>

            <Chip
              label={getStatusLabel(
                dwrData?.status
              )}
              size="small"
              color={getStatusColor(
                dwrData?.status
              )}
              sx={{
                height: 24,
                fontSize: '11px',
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>

        {/* =================================================
            BODY
        ================================================= */}

        <Box
          sx={{
            px: 2,
            py: 1.5,
            backgroundColor: '#fff',
            maxHeight:
              'calc(100vh - 245px)',
            overflowY: 'auto',
          }}
        >
          {loading ? (
            <Box
              sx={{
                minHeight: 300,
                display: 'flex',
                alignItems:
                  'center',
                justifyContent:
                  'center',
              }}
            >
              <CircularProgress
                size={28}
              />
            </Box>
          ) : !dwrData ? (
            <Box
              sx={{
                py: 8,
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: '#607d8b',
                }}
              >
                DWR information
                could not be
                found.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                border:
                  '1px solid #dfe5e8',
                borderRadius: '8px',
                backgroundColor:
                  '#f8fafb',
                px: 1.25,
                py: 0.5,
              }}
            >
              <DetailRow
                label="Project Manager"
                value={
                  getProjectManager()
                }
              />

              <DetailRow
                label="Submit Date"
                value={formatSubmitDate(
                  getValue(
                    dwrData?.submit_date,
                    dwrData?.submitDate,
                    dwrData?.createdAt,
                    dwrData?.created_at
                  )
                )}
              />

              <DetailRow
                label="Task Date"
                value={formatDate(
                  getValue(
                    dwrData?.task_date,
                    dwrData?.taskDate,
                    dwrData?.planned_date,
                    dwrData?.plannedDate
                  )
                )}
              />

              <DetailRow
                label="Cost Item"
                value={getCostItem()}
              />

              {/* =================================================
                  TASK / APPROVED HOURS
              ================================================= */}

              <Box
                sx={{
                  py: 1.05,
                  borderBottom:
                    '1px solid #e1e6e8',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns:
                      '1fr 1fr',
                    gap: 1.5,
                  }}
                >
                  {/* Task Hour */}
                  <Box>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: '#607d8b',
                        lineHeight: 1.2,
                        mb: 0.35,
                      }}
                    >
                      Task Hour
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: '#263238',
                        fontWeight: 600,
                        lineHeight: 1.35,
                      }}
                    >
                      {getTaskHour()}
                    </Typography>
                  </Box>

                  {/* Approved Hours */}
                  <Box
                    sx={{
                      pl: 1.5,
                      borderLeft:
                        '1px solid #dfe5e8',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: '#607d8b',
                        lineHeight: 1.2,
                        mb: 0.35,
                      }}
                    >
                      Approved Hours
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: 20,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '14px',
                          fontWeight: 700,
                          lineHeight: 1.35,
                          color:
                            Number(
                              dwrData?.status
                            ) === 1
                              ? '#2E7D32'
                              : '#78909C',
                        }}
                      >
                        {getApprovedHours()}
                        {Number(
                          dwrData?.status
                        ) === 1
                          ? ''
                          : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <DetailRow
                label="Employee Name"
                value={
                  getEmployeeName()
                }
              />

              <DetailRow
                label="Remarks"
                value={getRemarks()}
              />

              <DetailRow
                label="Description"
                value={
                  getDescription()
                }
                last
              />
            </Box>
          )}
        </Box>

        <Divider />

        {/* =================================================
            FOOTER
        ================================================= */}

        <Box
          sx={{
            px: 2,
            py: 1.25,
            display: 'flex',
            justifyContent:
              'flex-end',
            gap: 1,
            backgroundColor: '#fff',
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              minWidth: 66,
              height: 36,
              textTransform:
                'none',
              fontSize: '13px',
              fontWeight: 600,
              borderColor:
                '#63c6cf',
              color: '#4db6c0',
            }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            startIcon={
              <EditIcon />
            }
            onClick={handleEdit}
            disabled={
              !dwrData ||
              !isPending
            }
            sx={{
              minWidth: 80,
              height: 36,
              textTransform:
                'none',
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor:
                '#e6ebee',
              color: '#9aa8b5',
              boxShadow: 'none',

              '&:not(:disabled)': {
                backgroundColor:
                  '#63c6cf',
                color: '#fff',
              },
            }}
          >
            Edit
          </Button>

          <Button
            variant="contained"
            startIcon={
              <DeleteIcon />
            }
            onClick={handleDelete}
            disabled={
              !dwrData ||
              !isPending ||
              deleteLoading
            }
            sx={{
              minWidth: 84,
              height: 36,
              textTransform:
                'none',
              fontSize: '13px',
              fontWeight: 600,
              backgroundColor:
                '#e6ebee',
              color: '#9aa8b5',
              boxShadow: 'none',

              '&:not(:disabled)': {
                backgroundColor:
                  '#d9534f',
                color: '#fff',
              },
            }}
          >
            {deleteLoading
              ? 'Deleting...'
              : 'Delete'}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default StaffDwrView;